import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { ALIGNMENT_COLORS, ALIGNMENT_LABELS, TEAM_COLORS, TEAM_LABELS, getBaseCharacter } from "@cc/shared";
import type { GameView, ScriptCharacter, Team, WinningTeam } from "@cc/shared";

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** `override` wins when a seat's alignment has been overridden independent of its character. */
export function teamColor(team: Team | null, override?: WinningTeam | null): string {
  if (override) return ALIGNMENT_COLORS[override];
  return team ? (TEAM_COLORS[team] ?? "#555a6e") : "#555a6e";
}

export function teamLabel(team: Team | null, override?: WinningTeam | null): string {
  if (override) return ALIGNMENT_LABELS[override];
  return team ? (TEAM_LABELS[team] ?? "Unknown") : "Unknown";
}

/** Characters are looked up in the current script, which is the only roster a game ever uses. */
export function findCharacter(view: GameView | null, id: string | null | undefined): ScriptCharacter | null {
  if (!view || !id) return null;
  return view.script.characters.find((c) => c.id === id) ?? null;
}

/**
 * The wiki article for a character, or null for one the wiki cannot have (a custom-script
 * character is only known by the script that defined it).
 */
export function wikiUrl(character: ScriptCharacter): string | null {
  if (!getBaseCharacter(character.id)) return null;
  return `https://wiki.bloodontheclocktower.com/${encodeURIComponent(character.name.trim().replace(/\s+/g, "_"))}`;
}

export function characterName(view: GameView | null, id: string | null | undefined): string | null {
  return findCharacter(view, id)?.name ?? null;
}

export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}
